import type { ReactNode } from 'react';
import { Sparkles, Award } from 'lucide-react';
import { cn } from '../../lib/cn';

export type BadgeVariant =
  | 'neutral'
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'reconocimiento' // ámbar sobrio: coautoría / contribución destacada
  | 'ia'; // violeta: acento semántico de síntesis / hallazgo de IA

interface BadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
  /** Muestra el icono semántico propio de la variante (IA / Reconocimiento). */
  withIcon?: boolean;
}

const variants: Record<BadgeVariant, string> = {
  neutral: 'bg-surface-sunken text-content-muted border border-border',
  primary: 'bg-primary text-content-on-primary',
  success: 'text-success border border-success bg-transparent',
  warning: 'text-warning border border-warning bg-transparent',
  error: 'text-error border border-error bg-transparent',
  info: 'text-info border border-info bg-transparent',
  // Ámbar de reconocimiento, sobrio (chip claro + texto ámbar AA + borde).
  reconocimiento:
    'bg-recognition-soft text-[color:var(--color-recognition-text)] border border-recognition',
  // Violeta Inteligencia como acento semántico de IA (nunca wash de fondo).
  ia: 'text-violet border border-violet bg-transparent',
};

export function Badge({ variant = 'neutral', children, withIcon = false }: BadgeProps) {
  const icon =
    withIcon && variant === 'ia' ? (
      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
    ) : withIcon && variant === 'reconocimiento' ? (
      <Award className="h-3.5 w-3.5" aria-hidden="true" />
    ) : null;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-1',
        'font-sans text-xs font-semibold leading-none',
        variants[variant],
      )}
    >
      {icon}
      {children}
    </span>
  );
}
